import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, LogOut, UserPlus, Trash2, Users, Shield, Edit, LayoutGrid, List } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PermissionsSelector } from '@/components/PermissionsSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GestionUsuarios({ user, onLogout, updateUser }) {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'cards'
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null });
  const [nuevoUsuario, setNuevoUsuario] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    is_admin: false,
    trabajador_id: '',
    permissions: []
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadUsuarios();
    loadTrabajadores();
  }, []);

  const loadUsuarios = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsuarios(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const loadTrabajadores = async () => {
    try {
      const response = await axios.get(`${API}/trabajadores`);
      // Filtrar solo trabajadores activos
      const activos = response.data.filter(t => t.status === 'Activo');
      setTrabajadores(activos);
    } catch (error) {
      console.error('Error al cargar trabajadores:', error);
    }
  };

  const handleAddUser = async () => {
    setSaving(true);
    try {
      // Preparar datos del usuario, eliminando email si está vacío
      const userData = { ...nuevoUsuario };
      if (!userData.email || userData.email.trim() === '') {
        delete userData.email; // No enviar email si está vacío
      }
      
      const response = await axios.post(`${API}/auth/register`, userData);
      const userId = response.data.id; // Cambio: el backend devuelve el usuario directamente, no en user.id
      
      // Upload profile picture if selected
      if (profilePicture) {
        const formData = new FormData();
        formData.append('file', profilePicture);
        await axios.post(`${API}/users/${userId}/profile-picture`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Usuario creado exitosamente');
      setShowAddDialog(false);
      setNuevoUsuario({
        username: '',
        password: '',
        email: '',
        full_name: '',
        is_admin: false,
        trabajador_id: '',
        permissions: []
      });
      setProfilePicture(null);
      setProfilePicturePreview(null);
      await loadUsuarios(); // Esperar a que se recargue la lista
    } catch (error) {
      console.error('Error completo:', error.response?.data);
      
      // Manejar errores de validación de Pydantic
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Si es un array de errores de validación
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => {
            const field = err.loc?.join('.') || 'campo';
            return `${field}: ${err.msg}`;
          }).join(', ');
          toast.error(`Errores de validación: ${errorMessages}`);
        } 
        // Si es un string (ej: "Usuario ya existe")
        else if (typeof detail === 'string') {
          // Si el usuario ya existe, recargar la lista
          if (detail.includes('ya está registrado')) {
            toast.warning(detail);
            await loadUsuarios(); // Recargar para mostrar el usuario existente
            setShowAddDialog(false); // Cerrar el diálogo
          } else {
            toast.error(detail);
          }
        }
        // Si es otro tipo de objeto
        else {
          toast.error('Error al crear usuario. Verifica los datos ingresados.');
        }
      } else {
        toast.error('Error al crear usuario');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = async () => {
    setSaving(true);
    try {
      // Preparar datos del usuario, eliminando email si está vacío
      const updateData = {
        full_name: editingUser.full_name,
        is_admin: editingUser.is_admin,
        permissions: editingUser.permissions || [],
        contract_verification_code: editingUser.contract_verification_code
      };
      
      // Solo incluir email si no está vacío
      if (editingUser.email && editingUser.email.trim() !== '') {
        updateData.email = editingUser.email;
      }
      
      // Solo incluir password si se ingresó una nueva
      if (editingUser.newPassword && editingUser.newPassword.trim() !== '') {
        updateData.password = editingUser.newPassword;
      }
      
      await axios.put(`${API}/users/${editingUser.id}`, updateData);
      
      let updatedProfilePicture = editingUser.profile_picture;
      
      // Upload profile picture if selected
      if (profilePicture) {
        const formData = new FormData();
        formData.append('file', profilePicture);
        const response = await axios.post(`${API}/users/${editingUser.id}/profile-picture`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        updatedProfilePicture = response.data.profile_picture;
      }
      
      // If we're editing the current logged-in user, update the user state
      if (updateUser && editingUser.id === user.id) {
        const updatedUserData = {
          ...user,
          full_name: editingUser.full_name,
          email: editingUser.email,
          profile_picture: updatedProfilePicture
        };
        updateUser(updatedUserData);
      }
      
      toast.success('Usuario actualizado exitosamente');
      setShowEditDialog(false);
      setEditingUser(null);
      setProfilePicture(null);
      setProfilePicturePreview(null);
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (usuario) => {
    // Asegurar que permissions siempre sea un array y agregar campo para nueva contraseña
    const userWithDefaults = {
      ...usuario,
      permissions: usuario.permissions || [],
      email: usuario.email || '',
      newPassword: '' // Campo para cambiar contraseña (vacío por defecto)
    };
    setEditingUser(userWithDefaults);
    setProfilePicturePreview(usuario.profile_picture ? `${BACKEND_URL}${usuario.profile_picture}` : null);
    setShowEditDialog(true);
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePicture(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfilePicture = async () => {
    if (editingUser && editingUser.profile_picture) {
      try {
        await axios.delete(`${API}/users/${editingUser.id}/profile-picture`);
        setEditingUser({ ...editingUser, profile_picture: null });
        setProfilePicturePreview(null);
        setProfilePicture(null);
        
        // If we're editing the current logged-in user, update the user state
        if (updateUser && editingUser.id === user.id) {
          const updatedUserData = {
            ...user,
            profile_picture: null
          };
          updateUser(updatedUserData);
        }
        
        toast.success('Foto de perfil eliminada');
        loadUsuarios();
      } catch (error) {
        toast.error('Error al eliminar foto de perfil');
      }
    } else {
      setProfilePicture(null);
      setProfilePicturePreview(null);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/users/${deleteDialog.userId}`);
      toast.success('Usuario eliminado exitosamente');
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    } finally {
      setDeleteDialog({ open: false, userId: null });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-emerald-100 rounded-xl">
              <Users className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-emerald-900">Usuarios del Sistema</h2>
              <p className="text-sm text-gray-600">Total: {usuarios.length} usuarios</p>
            </div>
          </div>
          <div className="flex gap-2">
            {/* View Mode Toggle */}
            <div className="flex border rounded-lg overflow-hidden">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-emerald-600 hover:bg-emerald-700' : 'hover:bg-gray-100'}
                data-testid="view-list-button"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'cards' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('cards')}
                className={viewMode === 'cards' ? 'bg-emerald-600 hover:bg-emerald-700' : 'hover:bg-gray-100'}
                data-testid="view-cards-button"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
            <Button 
              onClick={() => setShowAddDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-user-button"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Agregar Usuario
            </Button>
          </div>
        </div>

        {/* Users Table or Cards */}
        {viewMode === 'list' ? (
          <Card className="border-0 shadow-md bg-white">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                <TableRow>
                  <TableHead>Nombre Completo</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Fecha de Creación</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      Cargando usuarios...
                    </TableCell>
                  </TableRow>
                ) : usuarios.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No hay usuarios registrados
                    </TableCell>
                  </TableRow>
                ) : (
                  usuarios.map((usuario) => (
                    <TableRow key={usuario.id} data-testid={`user-row-${usuario.id}`}>
                      <TableCell className="font-medium">{usuario.full_name}</TableCell>
                      <TableCell>{usuario.username}</TableCell>
                      <TableCell>{usuario.email}</TableCell>
                      <TableCell>
                        {usuario.is_admin ? (
                          <Badge className="bg-purple-100 text-purple-800 border-purple-200 border">
                            <Shield className="w-3 h-3 mr-1" />
                            Administrador
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800 border-gray-200 border">
                            Usuario
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(usuario.created_at).toLocaleDateString('es-ES')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(usuario)}
                            data-testid={`edit-user-${usuario.id}`}
                            className="border-blue-200 hover:bg-blue-50 text-blue-600"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {!usuario.is_admin && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDeleteDialog({ open: true, userId: usuario.id })}
                              data-testid={`delete-user-${usuario.id}`}
                              className="border-red-200 hover:bg-red-50 text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
        ) : (
          /* Cards View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-full text-center py-8 text-gray-500">
                Cargando usuarios...
              </div>
            ) : usuarios.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-500">
                No hay usuarios registrados
              </div>
            ) : (
              usuarios.map((usuario) => (
                <Card 
                  key={usuario.id} 
                  className="border-0 shadow-md hover:shadow-xl transition-shadow bg-white overflow-hidden"
                  data-testid={`user-card-${usuario.id}`}
                >
                  <CardContent className="p-6">
                    {/* Avatar Circle with Profile Picture or Initials */}
                    <div className="flex flex-col items-center mb-4">
                      {usuario.profile_picture ? (
                        <img 
                          src={`${BACKEND_URL}${usuario.profile_picture}`} 
                          alt={usuario.full_name}
                          className="w-24 h-24 rounded-full object-cover shadow-lg mb-3 border-2 border-emerald-200"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg mb-3">
                          <span className="text-3xl font-bold text-white">
                            {usuario.full_name.split(' ').slice(0, 2).map(n => n[0]).join('')}
                          </span>
                        </div>
                      )}
                      <h3 className="text-xl font-bold text-gray-900 text-center">
                        {usuario.full_name}
                      </h3>
                      <p className="text-sm text-gray-600 text-center">@{usuario.username}</p>
                    </div>

                    {/* Details */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Email</span>
                        <span className="text-sm font-semibold text-gray-900 truncate ml-2">{usuario.email}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Rol</span>
                        {usuario.is_admin ? (
                          <Badge className="bg-purple-100 text-purple-800 border-purple-200 border">
                            <Shield className="w-3 h-3 mr-1" />
                            Administrador
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800 border-gray-200 border">
                            Usuario
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Registrado</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {new Date(usuario.created_at).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(usuario)}
                        className="flex-1 border-blue-200 hover:bg-blue-50 text-blue-700"
                        data-testid={`edit-user-card-${usuario.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      {!usuario.is_admin && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteDialog({ open: true, userId: usuario.id })}
                          className="border-red-200 hover:bg-red-50 text-red-600"
                          data-testid={`delete-user-card-${usuario.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </main>

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agregar Nuevo Usuario</DialogTitle>
            <DialogDescription>
              Selecciona un trabajador y asigna sus credenciales de acceso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Selector de Trabajador */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Trabajador *</Label>
              <Select
                value={nuevoUsuario.trabajador_id || "none"}
                onValueChange={(value) => {
                  if (value === "none") {
                    setNuevoUsuario({ ...nuevoUsuario, trabajador_id: "", full_name: "" });
                  } else {
                    const trabajador = trabajadores.find(t => t.id === value);
                    if (trabajador) {
                      setNuevoUsuario({
                        ...nuevoUsuario,
                        trabajador_id: value,
                        full_name: `${trabajador.nombre} ${trabajador.primer_apellido} ${trabajador.segundo_apellido || ''}`.trim()
                      });
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un trabajador" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Selecciona un trabajador</SelectItem>
                  {trabajadores.map((trabajador) => (
                    <SelectItem key={trabajador.id} value={trabajador.id}>
                      {trabajador.nombre} {trabajador.primer_apellido} - {trabajador.ocupacion}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                El nombre y email se tomarán del trabajador seleccionado
              </p>
            </div>

            {/* Mostrar datos del trabajador seleccionado */}
            {nuevoUsuario.trabajador_id && nuevoUsuario.trabajador_id !== "none" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-medium text-blue-900">Trabajador seleccionado:</p>
                <p className="text-sm text-blue-700 mt-1">
                  <strong>Nombre Completo:</strong> {nuevoUsuario.full_name}
                </p>
              </div>
            )}

            {/* Credenciales de acceso */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-base font-semibold">Credenciales de Acceso</h3>
              
              <div className="space-y-2">
                <Label>Nombre de Usuario *</Label>
                <Input
                  value={nuevoUsuario.username}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, username: e.target.value })}
                  placeholder="Ej: jperez"
                  data-testid="new-user-username"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Contraseña *</Label>
                <Input
                  type="password"
                  value={nuevoUsuario.password}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, password: e.target.value })}
                  placeholder="Contraseña segura"
                  data-testid="new-user-password"
                />
              </div>
            </div>

            {/* Permisos */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-base font-semibold">Permisos y Acceso</h3>
              
              {/* Checkbox Admin */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="is_admin"
                  checked={nuevoUsuario.is_admin}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, is_admin: e.target.checked })}
                  className="rounded w-4 h-4"
                />
                <Label htmlFor="is_admin" className="cursor-pointer font-medium">
                  Usuario Administrador (Acceso completo a todos los módulos)
                </Label>
              </div>

              {/* Selector de Permisos */}
              {!nuevoUsuario.is_admin && (
                <div className="space-y-2">
                  <PermissionsSelector
                    selectedPermissions={nuevoUsuario.permissions}
                    onChange={(permissions) => setNuevoUsuario({ ...nuevoUsuario, permissions })}
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowAddDialog(false)}
              data-testid="cancel-add-user"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleAddUser}
              disabled={saving || !nuevoUsuario.trabajador_id || nuevoUsuario.trabajador_id === "none" || !nuevoUsuario.username || !nuevoUsuario.password}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-add-user"
            >
              {saving ? 'Guardando...' : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
            <DialogDescription>
              Modifica la información y permisos del usuario.
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-4">
              {/* Profile Picture Upload */}
              <div className="space-y-2">
                <Label>Foto de Perfil (Opcional)</Label>
                <div className="flex items-center gap-4">
                  {profilePicturePreview ? (
                    <div className="relative">
                      <img 
                        src={profilePicturePreview} 
                        alt="Preview" 
                        className="w-20 h-20 rounded-full object-cover border-2 border-emerald-200"
                      />
                      <Button
                        size="sm"
                        variant="destructive"
                        className="absolute -top-2 -right-2 rounded-full w-6 h-6 p-0"
                        onClick={removeProfilePicture}
                      >
                        ×
                      </Button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">
                        {editingUser.full_name ? editingUser.full_name.split(' ').slice(0, 2).map(n => n[0]).join('') : '?'}
                      </span>
                    </div>
                  )}
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      data-testid="edit-profile-picture-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG o GIF (Máx. 5MB)</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Nombre Completo</Label>
                <Input
                  value={editingUser.full_name}
                  onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                  placeholder="Nombre completo"
                  data-testid="edit-user-fullname"
                />
              </div>
              <div className="space-y-2">
                <Label>Email (Opcional)</Label>
                <Input
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  placeholder="correo@ejemplo.com (opcional)"
                  data-testid="edit-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Usuario</Label>
                <Input
                  value={editingUser.username}
                  disabled
                  className="bg-gray-100"
                  data-testid="edit-user-username"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Nueva Contraseña (Opcional)</Label>
                <Input
                  type="password"
                  value={editingUser.newPassword || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, newPassword: e.target.value })}
                  placeholder="Dejar vacío para no cambiar"
                  data-testid="edit-user-password"
                />
                <p className="text-xs text-gray-500">
                  Solo completa este campo si deseas cambiar la contraseña del usuario
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-is-admin"
                  checked={editingUser.is_admin}
                  onChange={(e) => setEditingUser({ ...editingUser, is_admin: e.target.checked })}
                  className="w-4 h-4 text-purple-600 rounded"
                  data-testid="edit-user-admin-checkbox"
                />
                <Label htmlFor="edit-is-admin" className="cursor-pointer flex items-center">
                  <Shield className="w-4 h-4 mr-2 text-purple-600" />
                  Usuario Administrador
                </Label>
              </div>
              
              {/* Permisos */}
              <div className="border-t pt-4 space-y-4">
                <h3 className="text-base font-semibold">Permisos y Acceso</h3>
                
                {/* Checkbox Admin */}
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="edit-is-admin-perm"
                    checked={editingUser.is_admin}
                    onChange={(e) => setEditingUser({ ...editingUser, is_admin: e.target.checked })}
                    className="rounded w-4 h-4"
                  />
                  <Label htmlFor="edit-is-admin-perm" className="cursor-pointer font-medium">
                    Usuario Administrador (Acceso completo a todos los módulos)
                  </Label>
                </div>

                {/* Selector de Permisos */}
                {!editingUser.is_admin && (
                  <div className="space-y-2">
                    <PermissionsSelector
                      selectedPermissions={editingUser.permissions || []}
                      onChange={(permissions) => setEditingUser({ ...editingUser, permissions })}
                    />
                  </div>
                )}
              </div>

              {/* Código de Verificación para Super Usuario (balpizaar) */}
              {editingUser.username === 'balpizaar' && (
                <div className="space-y-2 pt-4 border-t">
                  <Label className="text-amber-900 font-semibold flex items-center">
                    <Shield className="w-4 h-4 mr-2" />
                    Código de Verificación de Contratos
                  </Label>
                  <Input
                    type="text"
                    value={editingUser.contract_verification_code || '100892'}
                    onChange={(e) => setEditingUser({ ...editingUser, contract_verification_code: e.target.value })}
                    placeholder="Código de 6 dígitos"
                    maxLength={6}
                    className="text-center text-lg tracking-widest"
                  />
                  <p className="text-xs text-gray-500">Este código se requiere para modificar contratos existentes</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowEditDialog(false)}
              data-testid="cancel-edit-user"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleEditUser}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-edit-user"
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, userId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El usuario será eliminado permanentemente del sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-user">Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
